import { useState, useEffect, useCallback } from 'react';

const DESIGN_WIDTH = 1920;
const BASE_SCALE = 0.8;
const MIN_SCALE = 0.5;
const MAX_SCALE = 0.9;

/**
 * Масштаб макета пропорционален ширине окна относительно эталонных 1920.
 * Вся вёрстка считается от корневого font-size через pxToRem, поэтому
 * пропорции сохраняются на любой ширине и зуме, а не только на ступенях.
 */
export const applyUiScale = (width: number): void => {
  if (typeof document === 'undefined' || !width) {
    return;
  }
  const raw = (BASE_SCALE * width) / DESIGN_WIDTH;
  const scale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, raw));
  document.documentElement.style.setProperty('--ui-scale', scale.toFixed(4));
};

if (typeof window !== 'undefined') {
  applyUiScale(window.innerWidth);
}

export function useViewport() {
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(0);
  const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);
  const [keyboardHeight, setKeyboardHeight] = useState(0);

  const updateViewportMetrics = useCallback(() => {
    const hasVisualViewport = typeof window !== 'undefined' && 'visualViewport' in window;
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;

    setWidth(windowWidth);
    setHeight(windowHeight);

    const newHeight = hasVisualViewport ? window.visualViewport!.height : window.innerHeight;
    const heightDiff = Math.abs(windowHeight - newHeight);
    const isKeyboard = heightDiff > 150;

    setViewportHeight(newHeight);
    setIsKeyboardVisible(isKeyboard);
    setKeyboardHeight(isKeyboard ? heightDiff : 0);

    // Обновляем CSS-переменные
    document.documentElement.style.setProperty('--real-vh', `${newHeight}px`);
    document.documentElement.style.setProperty('--keyboard-height', `${isKeyboard ? heightDiff : 0}px`);
    document.documentElement.style.setProperty('--keyboard-visible', isKeyboard ? '1' : '0');
    applyUiScale(windowWidth);
  }, []);

  useEffect(() => {
    updateViewportMetrics();

    let resizeTimeout: number | null = null;
    const debouncedUpdate = () => {
      if (resizeTimeout) {
        window.cancelAnimationFrame(resizeTimeout);
      }
      resizeTimeout = window.requestAnimationFrame(updateViewportMetrics);
    };

    if (typeof window !== 'undefined' && window.visualViewport) {
      window.visualViewport.addEventListener('resize', debouncedUpdate);
      window.visualViewport.addEventListener('scroll', debouncedUpdate);
    }
    window.addEventListener('resize', debouncedUpdate);

    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', debouncedUpdate);
        window.visualViewport.removeEventListener('scroll', debouncedUpdate);
      }
      window.removeEventListener('resize', debouncedUpdate);
      if (resizeTimeout) {
        window.cancelAnimationFrame(resizeTimeout);
      }
    };
  }, [updateViewportMetrics]);

  return {
    width,
    height,
    viewportHeight,
    realVh: viewportHeight,
    isKeyboardVisible,
    keyboardHeight,
  };
}
